import type { PlanHighlight, PlanValueProp } from "@Light/services/lightTypes";
import { LightIcon } from "@Light/services/lightTypes";

export const MAX_RENEWAL_VALUE_PROPS = 3;

const HIGHLIGHT_ICONS: Record<string, LightIcon> = {
  bill_change: LightIcon.PiggyBank,
  rollover_credits: LightIcon.Coins,
  term: LightIcon.Clock,
};

// Temporary mapper until PCX-620 unifies renewal_highlights with presentation.value_propositions.
// Until then, renewal highlights lead and existing value props fill the remaining slots.

export function shownRenewalHighlights(
  highlights?: PlanHighlight[],
): PlanHighlight[] {
  if (!highlights?.length) return [];
  return [...highlights]
    .sort((a, b) => a.ranking - b.ranking)
    .slice(0, MAX_RENEWAL_VALUE_PROPS);
}

export function renewalHighlightsToValueProps(
  highlights?: PlanHighlight[],
  existing?: PlanValueProp[],
): PlanValueProp[] | undefined {
  if (highlights === undefined && existing === undefined) return undefined;

  const ranked = [...(highlights ?? [])].sort((a, b) => a.ranking - b.ranking);
  const seen = new Set<string>();
  const merged: PlanValueProp[] = [];
  for (const prop of [
    ...ranked.map(highlightToValueProp),
    ...(existing ?? []),
  ]) {
    if (seen.has(prop.id)) continue;
    seen.add(prop.id);
    merged.push(prop);
    if (merged.length === MAX_RENEWAL_VALUE_PROPS) break;
  }
  return merged.length ? merged : undefined;
}

function highlightToValueProp(highlight: PlanHighlight): PlanValueProp {
  return {
    id: highlight.id,
    icon: HIGHLIGHT_ICONS[highlight.id] ?? LightIcon.CheckCircle,
    text: valuePropText(highlight),
    callouts: [],
  };
}

function valuePropText(highlight: PlanHighlight): string {
  const { id, label, summary, hint } = highlight;
  if (id === "bill_change" && summary) {
    const cheaper = summary.startsWith("-");
    const amount = cheaper ? summary.slice(1) : summary;
    const basis = hint ? ` ${hint}` : "";
    return cheaper
      ? `Your average bill is estimated to decrease by ${amount}${basis}`
      : `Your average bill is estimated to increase by ${amount}${basis}`;
  }
  if (id === "rollover_credits" && summary) {
    return `Keep ${summary}`;
  }
  if (id === "term" && summary) {
    return `Lock in this rate for ${summary}`;
  }
  return [label, summary, hint].filter(Boolean).join(" ");
}
